import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    inject,
    Injector,
    input,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    ControlValueAccessor,
    FormBuilder,
    FormControl,
    FormControlDirective,
    FormControlName,
    FormGroupDirective,
    NG_VALUE_ACCESSOR,
    NgControl,
    ReactiveFormsModule,
    TouchedChangeEvent,
} from '@angular/forms';
import { JoinClassNamesPipe } from '@shared/join/join-class-names.pipe';
import { InputPipe } from '@shared/input/input.pipe';
import { InputType } from '@shared/input/input.type';
import { NgxMaskDirective } from 'ngx-mask';
import { filter, tap } from 'rxjs';

@Component({
    selector: 'companyid-input[placeholder]',
    imports: [JoinClassNamesPipe, ReactiveFormsModule, InputPipe, NgxMaskDirective],
    templateUrl: './input.component.html',
    styleUrl: './input.component.scss',
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: InputComponent,
            multi: true,
        },
    ],
})
export class InputComponent implements ControlValueAccessor, OnInit, AfterViewInit {
    public placeholder = input.required<string>();
    public label = input<string | null>(null);
    public mask = input<string | null>('');
    public textarea = input(false, {
        transform: (value: boolean | '') => value === '' || value,
    });
    public required = input(false, {
        transform: (value: boolean | '') => value === '' || value,
    });
    public type = input<InputType>('input');
    public classNames = input<string>('');

    private readonly fb = inject(FormBuilder);
    private readonly injector = inject(Injector);
    private readonly cdr = inject(ChangeDetectorRef);

    public control: FormControl<string | null> = this.fb.control(null);
    private readonly destroyRef = inject(DestroyRef);

    public ngOnInit(): void {
        const ngControl = this.injector.get(NgControl);
        if (ngControl instanceof FormControlName) {
            this.control = this.injector.get(FormGroupDirective).getControl(ngControl);
        } else {
            this.control = (ngControl as FormControlDirective).form as FormControl;
        }
    }

    public ngAfterViewInit(): void {
        this.control.events
            .pipe(
                filter((event): event is TouchedChangeEvent => {
                    return event instanceof TouchedChangeEvent;
                }),
                tap((event) => {
                    this.control.setErrors(event.source.errors);
                    this.cdr.detectChanges();
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public writeValue(): void {}

    public registerOnChange(fn: () => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public onChange: (value: string | boolean | null) => void = () => {};
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public onTouched: () => void = () => {};
}
